import {createReduxStore, register, useDispatch, useSelect} from '@wordpress/data';
import {apiFetch as API_FETCH, controls as dataControls} from '@wordpress/data-controls';

export const STORE_KEY = 'epi/landing';
const STORAGE_KEY = 'epi_landing_cache_v1';

const now = () => new Date().getTime();
const toMs = d => (d instanceof Date ? d.getTime() : d);

function isValidLandingPayload(p) {
    if (!p || Array.isArray(p) || typeof p !== 'object') return false;
    return (
        Array.isArray(p.topDeals) ||
        Array.isArray(p.categories) ||
        Array.isArray(p.recommendations) ||
        Array.isArray(p.shopByCategory)
    );
}

function lastThreeAm(before = new Date()) {
    const d = new Date(before);
    d.setHours(3, 0, 0, 0);
    if (before.getTime() < d.getTime()) {

        d.setDate(d.getDate() - 1);
    }
    return d;
}

function nextThreeAm(after = new Date()) {
    const d = new Date(after);
    d.setHours(3, 0, 0, 0);
    if (after.getTime() >= d.getTime()) {
        d.setDate(d.getDate() + 1);
    }
    return d;
}

function getStateSafe() {
    try {
        const sel = (typeof window !== 'undefined') ? window.wp?.data?.select?.(STORE_KEY) : null;
        const s = sel?.getState?.();
        return s || DEFAULT_STATE;
    } catch (e) {
        return DEFAULT_STATE;
    }
}

function loadPersisted() {
    try {
        if (typeof window === 'undefined') return {};
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
        return {};
    }
}

function savePersisted(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
    }
}

const persisted = loadPersisted();

const DEFAULT_STATE = {
    data: isValidLandingPayload(persisted.data) ? persisted.data : null,
    loading: false,
    error: null,
    fetchedAt: isValidLandingPayload(persisted.data) ? (persisted.fetchedAt || 0) : 0,
    nextRefreshAt: persisted.nextRefreshAt || toMs(nextThreeAm()),
    hash: typeof persisted.hash === 'string' ? persisted.hash : null,
};

const actions = {

    * fetchLandingHash() {

        const target = {path: '/api/landing/hash', expectJson: true};

        try {
            const result = yield API_FETCH({
                path: target.path,
                method: 'GET',
                headers: {'X-WP-Nonce': (typeof epiData !== 'undefined' ? epiData.nonce : undefined)}
            });
            const hash = (typeof result === 'string') ? result : (result?.hash || null);
            if (hash && typeof hash === 'string') {
                yield {type: 'SET_HASH', hash};
                return hash;
            }
            return null;
        } catch (e) {

            return null;
        }
    },

    * fetchLanding({force = false} = {}) {

        const prevState = getStateSafe();
        let effectiveForce = !!force;
        
        yield {type: 'LOADING'};

        try {
            const oldHash = prevState.hash || null;
            const newHash = yield actions.fetchLandingHash();
            if (oldHash === null) {
                effectiveForce = true;
            }
            if (newHash && oldHash && newHash !== oldHash) {
                effectiveForce = true;
            }

            if (!prevState.data && newHash && newHash !== oldHash) {
                effectiveForce = true;
            }
        } catch (e) {

        }

        const state = getStateSafe();
        const isStale = !state.fetchedAt || state.fetchedAt < toMs(lastThreeAm(new Date()));
        if (!effectiveForce && state.data && !isStale) {
            yield {type: 'DONE_LOADING'};
            return;
        }
        try {
            const target = {path: '/api/landing'};

            const payload = yield API_FETCH({
                path: target.path,
                headers: {'X-WP-Nonce': (typeof epiData !== 'undefined' ? epiData.nonce : undefined)}
            });

            if (!isValidLandingPayload(payload)) {
                yield {type: 'DONE_LOADING'};
                return;
            }

            yield {type: 'RECEIVE', payload};
        } catch (err) {
            yield {type: 'FAILED', error: err};
        }
    },

    * invalidate() {
        yield {type: 'INVALIDATE'};
        yield actions.fetchLanding({force: true});
    },
};

function reducer(state = DEFAULT_STATE, action) {
    switch (action.type) {
        case 'LOADING':
            return {...state, loading: true, error: null};
        case 'DONE_LOADING':
            return {...state, loading: false};
        case 'RECEIVE': {
            const next = {
                ...state,
                data: action.payload || null,
                loading: false,
                error: null,
                fetchedAt: now(),
            };
            savePersisted({
                data: next.data,
                fetchedAt: next.fetchedAt,
                nextRefreshAt: state.nextRefreshAt,
                hash: state.hash
            });
            return next;
        }
        case 'FAILED':
            return {...state, loading: false, error: action.error};
        case 'INVALIDATE':
            return {...state, data: null, fetchedAt: 0};
        case 'SET_NEXT_REFRESH_AT': {
            const next = {...state, nextRefreshAt: action.at};
            savePersisted({
                data: next.data,
                fetchedAt: next.fetchedAt,
                nextRefreshAt: next.nextRefreshAt,
                hash: state.hash
            });
            return next;
        }
        case 'SET_HASH': {
            const next = {...state, hash: action.hash};
            savePersisted({
                data: next.data,
                fetchedAt: next.fetchedAt,
                nextRefreshAt: next.nextRefreshAt,
                hash: next.hash
            });
            return next;
        }
        default:
            return state;
    }
}

const controls = {};
if (dataControls && dataControls.API_FETCH) {
    controls.API_FETCH = dataControls.API_FETCH;
}

export const store = createReduxStore(STORE_KEY, {
    reducer,
    actions,
    controls,
    selectors: {
        getState(s) {
            return s;
        },
        getData(s) {
            return s.data;
        },
        getTopDeals(s) {
            return s.data?.topDeals || [];
        },
        getCategories(s) {
            return (s.data?.categories || []).filter(c => Array.isArray(c?.products) && c.products.length > 0);
        },
        getRecommendations(s) {
            return (s.data?.recommendations || []).filter(c => Array.isArray(c?.products) && c.products.length > 0);
        },
        getShopByCategory(s) {
            return s.data?.shopByCategory || [];
        },
        getLoading(s) {
            return s.loading;
        },
        getError(s) {
            return s.error;
        },
    }
});

register(store);

export const useLanding = () => {
    const data = useSelect(select => select(STORE_KEY).getData(), []);
    const topDeals = useSelect(select => select(STORE_KEY).getTopDeals(), []);
    const categories = useSelect(select => select(STORE_KEY).getCategories(), []);
    const recommendations = useSelect(select => select(STORE_KEY).getRecommendations(), []);
    const shopByCategory = useSelect(select => select(STORE_KEY).getShopByCategory(), []);
    const loading = useSelect(select => select(STORE_KEY).getLoading(), []);
    const error = useSelect(select => select(STORE_KEY).getError(), []);
    const dispatch = useDispatch(STORE_KEY);
    return {
        data,
        topDeals,
        categories,
        recommendations,
        shopByCategory,
        loading,
        error,
        fetchLanding: (opts) => dispatch.fetchLanding(opts),
        invalidate: () => dispatch.invalidate(),
    };
};
